import type { ApiErrorBody } from "@/types";
import { loadTokens } from "./auth-client";

const BASE_URL =
  (import.meta.env.VITE_API_URL && import.meta.env.VITE_API_URL.replace(/\/$/, "")) || "";

export class ApiError extends Error {
  status: number;
  code: string;
  details?: unknown;

  constructor(status: number, body: ApiErrorBody | { message?: string }) {
    const code = "error" in body ? body.error.code : "UNKNOWN";
    const message = "error" in body ? body.error.message : body.message ?? "Request failed";
    super(message);
    this.status = status;
    this.code = code;
    this.details = "error" in body ? body.error.details : undefined;
  }
}

// AuthProvider registers a handler so a 401 from any request bounces the operator
// back through the OpenAuth login flow (token expired / revoked).
let onUnauthorized: (() => void) | null = null;
let unauthorizedHandled = false;

export function setOnUnauthorized(handler: (() => void) | null) {
  onUnauthorized = handler;
  if (!handler) unauthorizedHandled = false;
}

function handleUnauthorized() {
  if (onUnauthorized && !unauthorizedHandled) {
    unauthorizedHandled = true;
    onUnauthorized();
  }
}

export interface RequestOptions {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  body?: unknown;
  query?: Record<string, unknown>;
  signal?: AbortSignal;
}

function buildUrl(path: string, query?: Record<string, unknown>): string {
  let url: URL;
  if (path.startsWith("http://") || path.startsWith("https://")) {
    url = new URL(path);
  } else {
    url = new URL(path.startsWith("/") ? path : `/${path}`, window.location.origin);
    if (BASE_URL) {
      const base = new URL(BASE_URL, window.location.origin);
      url.protocol = base.protocol;
      url.host = base.host;
    }
  }
  if (query) {
    for (const [key, value] of Object.entries(query)) {
      if (value === undefined || value === null || value === "") continue;
      url.searchParams.set(key, String(value));
    }
  }
  return url.toString();
}

function authHeaders(): Record<string, string> {
  const tokens = loadTokens();
  return tokens?.access ? { Authorization: `Bearer ${tokens.access}` } : {};
}

function isFormData(body: unknown): body is FormData {
  return typeof FormData !== "undefined" && body instanceof FormData;
}

async function parseError(res: Response): Promise<ApiError> {
  const text = await res.text();
  try {
    const payload = text ? (JSON.parse(text) as unknown) : ({} as unknown);
    return new ApiError(res.status, payload as ApiErrorBody);
  } catch {
    return new ApiError(res.status, { message: text || res.statusText });
  }
}

export async function apiRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = "GET", body, query, signal } = options;

  const headers: Record<string, string> = { ...authHeaders() };

  let fetchBody: BodyInit | undefined;
  if (body === undefined) {
    fetchBody = undefined;
  } else if (isFormData(body)) {
    fetchBody = body;
  } else {
    headers["Content-Type"] = "application/json";
    fetchBody = JSON.stringify(body);
  }

  const res = await fetch(buildUrl(path, query), {
    method,
    headers,
    body: fetchBody,
    signal,
  });

  if (res.status === 401) handleUnauthorized();

  if (res.status === 204) return undefined as T;

  const text = await res.text();
  const payload = text ? (JSON.parse(text) as unknown) : ({} as unknown);

  if (!res.ok) {
    throw new ApiError(res.status, payload as ApiErrorBody);
  }
  return payload as T;
}

export async function apiFetchBlob(
  path: string,
  options: Omit<RequestOptions, "body"> = {}
): Promise<{ blob: Blob; fileName: string; contentType: string }> {
  const { method = "GET", query, signal } = options;
  const headers: Record<string, string> = { ...authHeaders() };

  const res = await fetch(buildUrl(path, query), { method, headers, signal });
  if (res.status === 401) handleUnauthorized();
  if (!res.ok) throw await parseError(res);

  const blob = await res.blob();
  const disposition = res.headers.get("Content-Disposition") ?? "";
  const match = /filename="?([^";]+)"?/.exec(disposition);
  const fileName = match?.[1] ?? "download";
  const contentType = res.headers.get("Content-Type") ?? blob.type ?? "application/octet-stream";
  return { blob, fileName, contentType };
}
